import {
  Component,
  ChangeDetectionStrategy,
  inject,
  DestroyRef,
  ElementRef,
  viewChild,
  effect,
} from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { TranslateModule } from "@ngx-translate/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { fromEvent, debounceTime, distinctUntilChanged, map } from "rxjs";

/**
 * Custom Formly field type for search input with icon.
 * Features debounced input (300ms) to avoid excessive updates.
 */
@Component({
  selector: "formly-search-input",
  imports: [ReactiveFormsModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="mb-3">
      @if (props.label) {
        <h5 class="font-alternate text-sm font-semibold text-black tracking-tight mb-1.5">
          {{ props.label | translate }}
        </h5>
      }
      <div class="flex rounded border border-gray-200 overflow-hidden focus-within:ring-1 focus-within:ring-mty-primary focus-within:border-mty-primary transition-shadow">
        <input
          #inputRef
          type="text"
          class="flex-1 min-w-0 px-3 py-1.5 text-sm border-0 outline-none bg-white"
          [formControl]="formControl"
          [placeholder]="(props.placeholder || 'Search...') | translate"
        />
        <span class="inline-flex items-center px-2.5 text-gray-400 bg-gray-50 border-l border-gray-200">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
        </span>
      </div>
    </div>
  `,
})
export class SearchInputFieldType extends FieldType<FieldTypeConfig> {
  private readonly destroyRef = inject(DestroyRef);

  inputRef = viewChild<ElementRef<HTMLInputElement>>("inputRef");

  constructor() {
    super();
    effect(() => {
      const inputEl = this.inputRef();
      if (inputEl) {
        this.setupDebouncedInput(inputEl.nativeElement);
      }
    });
  }

  private setupDebouncedInput(element: HTMLInputElement): void {
    fromEvent(element, "input")
      .pipe(
        debounceTime(300),
        map(() => element.value),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((value) => {
        this.formControl.setValue(value);
      });
  }
}
